import React, { FC, useCallback } from 'react';
import { Box, Button, List, ListItem, ListItemText, Paper, Typography, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SkipsProcessingJobToProcess } from '../domain/SkipsProcessingJobToProcess';

interface SkipsProcessingSidebarProps {
  jobs: SkipsProcessingJobToProcess[];
  activeJobId: string | null;
  isLoading: boolean;
  onRefresh: () => Promise<void>;
  onJobClick: (jobId: string) => Promise<void>;
}

export const SkipsProcessingSidebar: FC<SkipsProcessingSidebarProps> = ({
  jobs,
  activeJobId,
  isLoading,
  onRefresh,
  onJobClick,
}) => {
  const handleRefresh = useCallback(async () => {
    await onRefresh();
  }, [onRefresh]);

  const handleJobClick = useCallback(
    async (jobId: string) => {
      await onJobClick(jobId);
    },
    [onJobClick],
  );

  const getBackgroundColor = (job: SkipsProcessingJobToProcess): string => {
    if (job.id === activeJobId) {
      return '#ffe0b2'; // orange
    }

    return 'transparent';
  };

  const isJobActive = (job: SkipsProcessingJobToProcess): boolean => {
    return job.id === activeJobId;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: 350,
        height: 'calc(100vh - 40px)', // Account for app bar height
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 40, // Start below the app bar
        zIndex: 1, // Lower z-index so map UI elements appear on top
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Skips Processing
        </Typography>

        <Button
          variant="outlined"
          startIcon={isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          fullWidth
          onClick={handleRefresh}
          disabled={isLoading}
        >
          Refresh Jobs
        </Button>
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', overscrollBehavior: 'contain' }}>
        {jobs.length === 0 && !isLoading && (
          <ListItem>
            <ListItemText primary="No jobs to process" secondary="Click refresh to load jobs" />
          </ListItem>
        )}

        {jobs.map((job) => (
          <ListItem
            key={job.id}
            disablePadding
            sx={{
              backgroundColor: getBackgroundColor(job),
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              transition: 'background-color 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', width: '100%', p: 2, flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: isJobActive(job) ? 'bold' : 'normal', mb: 1 }}>
                {job.name}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                {isJobActive(job) && (
                  <Typography variant="body2" color="primary">
                    Active
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Operator: {job.operator}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sample Date: {job.sampleDate}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleJobClick(job.id)}
                disabled={isJobActive(job)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {isJobActive(job) ? 'Loaded' : 'Load'}
              </Button>
            </Box>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};
